/**
 * Middleware for the device handoff feature.
 *
 * Bridges DeviceHandoffService events to Redux actions,
 * and handles incoming WebSocket handoff messages from the server.
 */
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { hangup } from '../base/connection/actions';
import { CONFERENCE_JOINED } from '../base/conference/actionTypes';
import { getRoomName } from '../base/conference/functions';
import { TRACK_ADDED, TRACK_UPDATED } from '../base/tracks/actionTypes';
import { isHandoffVideoReady } from './mediaReadiness';
import { handoffService } from './DeviceHandoffService';
import { queueService } from '../interpreter-queue/InterpreterQueueService';
import { acceptHandoff, handoffDeviceFound, handoffDeviceLost, handoffServerDevicesUpdated, handoffProximitySample, handoffStarted, handoffProgressUpdate, handoffCompleted, handoffFailed, handoffReceived, handoffAccepted, handoffDeclined, handoffInterpreterNotify, handoffInterpreterComplete } from './actions';
import { getUserRole } from '../base/user-role/functions';
import { isObserverRoomPage } from '../vrs-auth/observerRoomPage';
import { removePersistentItem } from '../vrs-auth/storage';
MiddlewareRegistry.register((store) => (next) => (action) => {
    const result = next(action);
    // A manager's hidden observation can neither take nor give the call:
    // no handoff listeners, offers, releases or stored-call changes.
    if (isObserverRoomPage()) {
        return result;
    }
    // Initialize handoff service listeners once
    if (!store._deviceHandoffInitialized) {
        const initialized = initializeDeviceHandoff(store);
        if (initialized) {
            store._deviceHandoffInitialized = true;
        }
    }
    // The server's handoff messages, once for each queue listener
    // generation: a sign-out removes every queue listener
    // (queueService.disconnect) while the app stays open, so the next
    // sign-in registers them again (Robert, 2026-10-29).
    if (store._deviceHandoffQueueListeners !== queueService.listenerGeneration()) {
        store._deviceHandoffQueueListeners = queueService.listenerGeneration();
        listenForServerHandoffMessages(store);
    }
    if (action.type === CONFERENCE_JOINED || action.type === TRACK_ADDED || action.type === TRACK_UPDATED) {
        const state = store.getState();
        const roomName = getRoomName(state);
        if (action.type === CONFERENCE_JOINED && roomName
            && action.conference === state['features/base/conference'].conference) {
            handoffService.noteConferenceJoined(roomName);
        }
        if (roomName) {
            void handoffService.confirmTrackEstablished(roomName, () => isHandoffVideoReady(store.getState(), roomName));
        }
    }
    return result;
});
/**
 * Wire up DeviceHandoffService events to Redux dispatches.
 */
function initializeDeviceHandoff(store) {
    const userRole = getUserRole();
    console.log('[DeviceHandoff] Initializing for role:', userRole);
    // Companion device discovery
    handoffService.on('device_found', (device) => {
        store.dispatch(handoffDeviceFound(device));
    });
    handoffService.on('device_lost', (data) => {
        store.dispatch(handoffDeviceLost(data.id));
    });
    handoffService.on('proximity_sample', (device) => {
        store.dispatch(handoffProximitySample(device));
    });
    // Handoff progress
    handoffService.on('handoff_started', (data) => {
        store.dispatch(handoffStarted(data.companionDevice));
    });
    handoffService.on('handoff_progress', (progress) => {
        store.dispatch(handoffProgressUpdate(progress));
    });
    handoffService.on('handoff_completed', (data) => {
        store.dispatch(handoffCompleted(data.roomName));
    });
    handoffService.on('handoff_failed', (data) => {
        store.dispatch(handoffFailed(data.error || data.message || 'Handoff failed'));
    });
    handoffService.on('handoff_target_abort', () => {
        removePersistentItem('vrs_active_call');
        store.dispatch(hangup(false));
    });
    handoffService.on('handoff_source_timeout', () => {
        const roomName = getRoomName(store.getState());
        if (roomName) {
            queueService.registerHandoffSession(roomName, handoffService.getDeviceId());
        }
    });
    // Receiving device events
    handoffService.on('handoff_received', (data) => {
        store.dispatch(handoffReceived(data.token));
    });
    handoffService.on('handoff_accepted', (data) => {
        store.dispatch(handoffAccepted(data));
    });
    handoffService.on('handoff_declined', (data) => {
        store.dispatch(handoffDeclined(data.token));
    });
    if (typeof window !== 'undefined') {
        const token = handoffService.parseHandoffUrl(window.location.href);
        if (token) {
            void handoffService.receiveHandoff(token);
        }
    }
    console.log('[DeviceHandoff] Middleware initialized');
    return true;
}
/**
 * WebSocket handoff messages from the server (routed through queueService).
 */
function listenForServerHandoffMessages(store) {
    queueService.on('handoff_in_progress', (data) => {
        store.dispatch(handoffInterpreterNotify(data));
    });
    queueService.on('handoff_complete', (data) => {
        store.dispatch(handoffInterpreterComplete(data));
    });
    queueService.on('handoff_consumed', (data) => {
        if (data?.targetDeviceId && data.targetDeviceId !== handoffService.getDeviceId()) {
            return;
        }
        if (!data?.handoffToken || !handoffService.noteSourceHandoffConsumed(data.handoffToken)) {
            return;
        }
        store.dispatch(handoffProgressUpdate({
            stage: 'establishing',
            message: 'Target device is joining...'
        }));
    });
    queueService.on('handoff_release', (data) => {
        if (!data?.targetDeviceId || data.targetDeviceId !== handoffService.getDeviceId()) {
            return;
        }
        if (!data?.handoffToken
            || !handoffService.handleSourceRelease(data.roomName || '', data.handoffToken)) {
            return;
        }
        // This is a local exit only. Sending call_end here would tear down the
        // shared backend call that the target device just took over.
        removePersistentItem('vrs_active_call');
        store.dispatch(hangup(false));
    });
    queueService.on('handoff_cancelled', (data) => {
        if (data?.token) {
            const cancelledRole = handoffService.handleCancellation(data.token);
            if (cancelledRole === 'source') {
                const roomName = getRoomName(store.getState());
                if (roomName) {
                    queueService.registerHandoffSession(roomName, handoffService.getDeviceId());
                }
            }
        }
    });
    queueService.on('handoff_executed', (data) => {
        store.dispatch(handoffAccepted(data));
    });
    queueService.on('handoff_prepared', (data) => {
        store.dispatch(handoffProgressUpdate({
            stage: 'transferring',
            message: 'Handoff token created'
        }));
    });
    queueService.on('handoff_error', (data) => {
        store.dispatch(handoffFailed(data?.message || 'Handoff error from server'));
    });
    queueService.on('handoff_devices_updated', (data) => {
        store.dispatch(handoffServerDevicesUpdated(Array.isArray(data?.devices) ? data.devices : []));
    });
    queueService.on('handoff_offer', (data) => {
        if (!data?.token || handoffService.isHandoffInProgress()) {
            return;
        }
        if (data.targetDeviceId && data.targetDeviceId !== handoffService.getDeviceId()) {
            return;
        }
        void handoffService.receiveHandoff(data.token);
        if (data.autoProximity) {
            store.dispatch(acceptHandoff(data.token));
        }
    });
}
/**
 * Handle handoff-related WebSocket messages from the server.
 * Called from InterpreterQueueService when it receives handoff messages.
 */
export function handleHandoffWsMessage(store, message) {
    switch (message.type) {
        case 'handoff_in_progress':
            store.dispatch(handoffInterpreterNotify(message.data));
            break;
        case 'handoff_complete':
            store.dispatch(handoffInterpreterComplete(message.data));
            break;
        case 'handoff_consumed':
            if (message.data?.targetDeviceId
                && message.data.targetDeviceId !== handoffService.getDeviceId()) {
                return;
            }
            if (!message.data?.handoffToken
                || !handoffService.noteSourceHandoffConsumed(message.data.handoffToken)) {
                return;
            }
            // Original device got confirmation that target consumed the token
            store.dispatch(handoffProgressUpdate({
                stage: 'establishing',
                message: 'Target device is joining...'
            }));
            break;
        case 'handoff_release':
            if (message.data?.targetDeviceId === handoffService.getDeviceId()
                && message.data?.handoffToken
                && handoffService.handleSourceRelease(message.data?.roomName || '', message.data.handoffToken)) {
                removePersistentItem('vrs_active_call');
                store.dispatch(hangup(false));
            }
            break;
        case 'handoff_cancelled':
            if (message.data?.token) {
                handoffService.handleCancellation(message.data.token);
            }
            break;
        case 'handoff_executed':
            // This device successfully executed the handoff
            store.dispatch(handoffAccepted(message.data));
            break;
        case 'handoff_offer':
            if (message.data?.token) {
                if (message.data.targetDeviceId && message.data.targetDeviceId !== handoffService.getDeviceId()) {
                    return;
                }
                store.dispatch(handoffReceived(message.data.token));
            }
            break;
        case 'handoff_devices_updated':
            store.dispatch(handoffServerDevicesUpdated(Array.isArray(message.data?.devices) ? message.data.devices : []));
            break;
    }
}
